import React from 'react';
import type { PlaybookStatusFull } from '../../../types/playbook.types';
import { listAllDocumentTypeOptions } from '../../../../dms/taxonomy/legalDocumentTaxonomy';

interface ConfigFormProps {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  statuses?: PlaybookStatusFull[];
}

const inputClass = 'w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';
const labelClass = 'block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5';
const selectClass = inputClass;

// ── 1. role_required ──
export const RoleRequiredForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Required Role</label>
      <input
        type="text"
        className={inputClass}
        value={(config.role as string) ?? ''}
        onChange={(e) => onChange({ ...config, role: e.target.value })}
        placeholder="e.g. attorney, paralegal, admin"
      />
    </div>
  </div>
);

// ── 2. assignee_only ──
export const AssigneeOnlyForm: React.FC<ConfigFormProps> = () => (
  <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">
    No configuration needed — only the assigned user can execute this transition.
  </p>
);

// ── 3. status_is ──
export const StatusIsForm: React.FC<ConfigFormProps> = ({ config, onChange, statuses }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Required Status</label>
      <select
        className={selectClass}
        value={(config.status_slug as string) ?? ''}
        onChange={(e) => onChange({ ...config, status_slug: e.target.value })}
      >
        <option value="">Select status...</option>
        {(statuses ?? []).map((s) => (
          <option key={s.id} value={s.slug}>{s.name}</option>
        ))}
      </select>
    </div>
  </div>
);

// ── 4. field_has_value ──
export const FieldHasValueForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Target</label>
      <select
        className={selectClass}
        value={(config.target as string) ?? 'task'}
        onChange={(e) => onChange({ ...config, target: e.target.value })}
      >
        <option value="task">Task</option>
        <option value="matter">Matter</option>
      </select>
    </div>
    <div>
      <label className={labelClass}>Field Name</label>
      <input
        type="text"
        className={inputClass}
        value={(config.field as string) ?? ''}
        onChange={(e) => onChange({ ...config, field: e.target.value })}
        placeholder="e.g. assignee_id, priority"
      />
    </div>
    <div>
      <label className={labelClass}>Operator</label>
      <select
        className={selectClass}
        value={(config.operator as string) ?? 'equals'}
        onChange={(e) => onChange({ ...config, operator: e.target.value })}
      >
        <option value="equals">Equals</option>
        <option value="not_equals">Not Equals</option>
        <option value="exists">Exists (not null)</option>
        <option value="contains">Contains</option>
        <option value="greater_than">Greater Than</option>
        <option value="less_than">Less Than</option>
      </select>
    </div>
    {config.operator !== 'exists' && (
      <div>
        <label className={labelClass}>Value</label>
        <input
          type="text"
          className={inputClass}
          value={(config.value as string) ?? ''}
          onChange={(e) => onChange({ ...config, value: e.target.value })}
        />
      </div>
    )}
  </div>
);

// ── 5. expression ──
export const ExpressionGuardForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Expression</label>
      <textarea
        className={`${inputClass} font-mono`}
        rows={3}
        value={(config.expression as string) ?? ''}
        onChange={(e) => onChange({ ...config, expression: e.target.value })}
        placeholder="e.g. context.task.priority === 'high'"
      />
      <p className="text-[9px] text-gray-400 mt-0.5">
        JavaScript expression. Access context.task, context.matter, context.instance.
      </p>
    </div>
  </div>
);

const documentTypeOptions = listAllDocumentTypeOptions();

// ── 6. document_attached ──
export const DocumentAttachedForm: React.FC<ConfigFormProps> = ({ config, onChange }) => {
  const value = (config.document_type as string) || (config.doc_type as string) || '';

  return (
    <div className="space-y-2">
      <div>
        <label className={labelClass} htmlFor="playbook-guard-document-type">Document Type</label>
        <select
          id="playbook-guard-document-type"
          className={selectClass}
          value={value}
          onChange={(e) => onChange({ ...config, document_type: e.target.value, doc_type: e.target.value })}
        >
          <option value="">Any document</option>
          {documentTypeOptions.map((documentType) => (
            <option key={documentType.value} value={documentType.playbookEventKey}>
              {documentType.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

// ── 7. dependency_complete ──
export const DependencyCompleteForm: React.FC<ConfigFormProps> = () => (
  <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">
    No configuration needed — checks that all dependency tasks are complete.
  </p>
);

// ── 8. playbook_status ──
export const PlaybookStatusForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Playbook Slug</label>
      <input
        type="text"
        className={inputClass}
        value={(config.playbook_slug as string) ?? ''}
        onChange={(e) => onChange({ ...config, playbook_slug: e.target.value })}
        placeholder="e.g. family-law-dissolution"
      />
    </div>
    <div>
      <label className={labelClass}>Status Slug</label>
      <input
        type="text"
        className={inputClass}
        value={(config.status_slug as string) ?? ''}
        onChange={(e) => onChange({ ...config, status_slug: e.target.value })}
        placeholder="e.g. filed"
      />
    </div>
    <div>
      <label className={labelClass}>Operator</label>
      <select
        className={selectClass}
        value={(config.operator as string) ?? 'equals'}
        onChange={(e) => onChange({ ...config, operator: e.target.value })}
      >
        <option value="equals">Equals</option>
        <option value="not_equals">Not Equals</option>
        <option value="past">Has Passed</option>
      </select>
    </div>
  </div>
);

// ── 9. approval_received ──
export const ApprovalReceivedForm: React.FC<ConfigFormProps> = () => (
  <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">
    No configuration needed — checks approval chain status from the approval entity.
  </p>
);

// ── Guard Form Router ──
const GUARD_FORMS: Record<string, React.FC<ConfigFormProps>> = {
  role_required: RoleRequiredForm,
  assignee_only: AssigneeOnlyForm,
  status_is: StatusIsForm,
  field_has_value: FieldHasValueForm,
  expression: ExpressionGuardForm,
  document_attached: DocumentAttachedForm,
  dependency_complete: DependencyCompleteForm,
  playbook_status: PlaybookStatusForm,
  approval_received: ApprovalReceivedForm,
};

interface GuardConfigFormProps extends ConfigFormProps {
  guardType: string;
}

export const GuardConfigForm: React.FC<GuardConfigFormProps> = ({ guardType, ...rest }) => {
  const Form = GUARD_FORMS[guardType];
  if (!Form) return <p className="text-[10px] text-gray-400 italic">Unknown guard type: {guardType}</p>;
  return <Form {...rest} />;
};
